import { useState, useMemo, useCallback, useEffect } from 'react';
import { Head, router, usePage } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';
import { Breadcrumbs } from '../../components/products/Breadcrumbs';
import { SearchBar } from '../../components/products/SearchBar';
import { ProductImage } from '../../components/products/ProductImage';
import { HealthBenefits } from '../../components/products/HealthBenefits';
import { DeliveryInfo } from '../../components/products/DeliveryInfo';
import { GroupedRelatedProducts } from '../../components/products/GroupedRelatedProducts';
import { MobileToolbar } from '../../components/products/MobileToolbar';
import { FormatSelector } from '../../components/products/FormatSelector';
import { ProductHighlight } from '../../components/products/ProductHighlight';
import { QuantityInput } from '../../components/products/QuantityInput';
import { EditableField } from '../../components/products/EditableField';
import { useAdminContentEdit } from '../../hooks/useAdminContentEdit';
import { getCSRFToken } from '../../utils/csrf';
import { dispatchCartCount } from '../../utils/cartBadge';
import { route } from '../../utils/route';
import { trackViewItem, gtagAsync } from '../../utils/tracking';
import type { ProductShowProps, ProductFormat } from '../../types/products';

function formatPrice(price: number): string {
  return price.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function Show({
  product,
  selectedFormatId,
  relatedProducts,
  relatedSections,
  deliveryPrice,
  deliveryOptions,
  userDelivery,
  categories,
  structuredDataJson,
}: ProductShowProps) {
  // Admin inline editing
  const { auth } = usePage<{ auth: { user: { isAdmin: boolean } | null } }>().props;
  const isAdmin = auth.user?.isAdmin ?? false;

  const contentEdit = useAdminContentEdit({
    productId: product.id,
    initialValues: {
      title: product.title,
      description: product.description ?? '',
    },
    initialFormatPrices: product.formats.map(f => ({ id: f.id, price: f.price })),
    onSaved: () => router.reload({ only: ['product'], preserveScroll: true }),
  });

  // Determine initial format: from URL param, default, or first
  const initialFormat = useMemo(() => {
    if (selectedFormatId) {
      const found = product.formats.find(f => f.id === selectedFormatId);
      if (found) return found;
    }
    return product.default_format ?? product.formats[0] ?? null;
  }, [product, selectedFormatId]);

  const [selectedFormat, setSelectedFormat] = useState<ProductFormat | null>(initialFormat);
  const [quantity, setQuantity] = useState(0);

  useEffect(() => {
    trackViewItem({
      item_id: product.id,
      item_name: product.title,
      item_category: product.category_name,
      price: initialFormat?.discounted_price,
    });
  }, [product.id]);
  const [isAdding, setIsAdding] = useState(false);
  const [added, setAdded] = useState(false);

  const subtotal = useMemo(() => {
    if (!selectedFormat || quantity === 0) return 0;
    return selectedFormat.discounted_price * quantity;
  }, [selectedFormat, quantity]);

  const handleAddToCart = useCallback(async () => {
    if (!selectedFormat || quantity === 0) return;
    setIsAdding(true);
    try {
      const response = await fetch(route('api.cart.add_item'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-XSRF-TOKEN': getCSRFToken(),
        },
        credentials: 'same-origin',
        body: JSON.stringify({ id: product.id, format_id: selectedFormat.id, quantity }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Error al agregar al carrito');
      }

      const data = await response.json();

      // GA4 tracking (async to avoid blocking INP)
      gtagAsync('event', 'add_to_cart', {
        currency: 'ARS',
        items: [{ item_id: product.id, quantity }],
      });

      // Update cart badge via event (Navbar listens to 'cart-updated')
      if (data.summary?.count != null) {
        dispatchCartCount(data.summary.count);
      }

      setAdded(true);
      setQuantity(0);
      setTimeout(() => setAdded(false), 2000);
    } catch {
      // error handled upstream
    } finally {
      setIsAdding(false);
    }
  }, [selectedFormat, quantity, product.id]);

  const handleRelatedAddToCart = useCallback(async (productId: number, formatId: number, qty: number) => {
    const response = await fetch(route('api.cart.add_item'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': getCSRFToken(),
      },
      credentials: 'same-origin',
      body: JSON.stringify({ id: productId, format_id: formatId, quantity: qty }),
    });
    if (!response.ok) throw new Error('Error al agregar al carrito');
    const data = await response.json();
    if (data.summary?.count != null) {
      dispatchCartCount(data.summary.count);
    }
  }, []);

  // Search: navigate to /productos/{category} with search term
  const [searchTerm, setSearchTerm] = useState('');
  const handleSearchSubmit = useCallback((term: string) => {
    router.visit(`${route('products.index', { cat: product.category_slug })}?p=${encodeURIComponent(term)}`);
  }, [product.category_slug]);

  // AI description generation
  const [isGenerating, setIsGenerating] = useState(false);
  const handleGenerateDescription = useCallback(async () => {
    if (isGenerating) return;
    setIsGenerating(true);
    try {
      const currentDesc = contentEdit.getDisplayValue('description');
      const response = await fetch('/admin/completions/product-description', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-XSRF-TOKEN': getCSRFToken(),
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          name: product.title,
          category_id: product.category_id,
          current_description: currentDesc.trim() || null,
        }),
      });
      const data = await response.json();
      if (data.success && data.description) {
        contentEdit.updateField('description', data.description);
      }
    } catch {
      // silent fail
    } finally {
      setIsGenerating(false);
    }
  }, [isGenerating, product.title, product.category_id, contentEdit]);

  const descriptionMeta = product.description
    ? product.description.substring(0, 155)
    : `Compra ${product.title} con delivery en Córdoba. ${product.category_name} de primera calidad. Venta mayorista y minorista.`;

  const pageUrl = route('product.show', { slug: product.slug });

  return (
    <MainLayout>
      <Head>
        <title>{`${product.title} | Abasto Virtual Córdoba`}</title>
        <meta name="description" content={descriptionMeta} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href={pageUrl} />
        {product.has_image && (
          <link rel="preload" as="image" href={product.webp_image_url} type="image/webp" />
        )}
        <meta property="og:type" content="product" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={`${product.title} - ${product.category_name} | Abasto Virtual`} />
        <meta property="og:description" content={descriptionMeta} />
        {product.has_image && <meta property="og:image" content={product.image_url} />}
        <meta property="og:locale" content="es_AR" />
        <meta property="og:site_name" content="Abasto Virtual" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={`${product.title} - Abasto Virtual`} />
        <meta property="twitter:description" content={descriptionMeta} />
        {product.has_image && <meta property="twitter:image" content={product.image_url} />}
        <script type="application/ld+json">{JSON.stringify(structuredDataJson)}</script>
      </Head>

      <div className="mx-auto max-w-6xl px-4 py-6 pb-24 sm:pb-6">
        <Breadcrumbs
          items={[
            { name: 'Inicio', url: route('index') },
            { name: 'Productos', url: route('products.index') },
            { name: product.category_name, url: route('products.index', { cat: product.category_slug }) },
          ]}
          currentPage={product.title}
        />

        {/* Desktop search bar */}
        <div className="hidden sm:block">
          <SearchBar value={searchTerm} onChange={setSearchTerm} onSubmit={handleSearchSubmit} />
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Left: Image */}
          <ProductImage
            title={product.title}
            categoryName={product.category_name}
            imageUrl={product.image_url}
            webpImageUrl={product.webp_image_url}
            hasImage={product.has_image}
            isEditMode={contentEdit.isEditMode}
            imagePreviewUrl={contentEdit.imagePreviewUrl}
            onImageSelect={contentEdit.setImage}
          />

          {/* Right: Info */}
          <div>
            <ProductHighlight highlight={product.highlight} />
            <EditableField
              tag="h1"
              value={contentEdit.getDisplayValue('title')}
              onChange={(v) => contentEdit.updateField('title', v)}
              isEditMode={contentEdit.isEditMode}
              isDirty={contentEdit.isFieldDirty('title')}
              className="mb-2 text-2xl font-medium text-gray-800 md:text-3xl"
              inputClassName="text-2xl font-medium text-gray-800 md:text-3xl"
            >
              {contentEdit.getDisplayValue('title')}
            </EditableField>

            {/* Price */}
            {selectedFormat && (
              <div className="mb-4">
                <span className="text-3xl font-bold text-gray-900">
                  {selectedFormat.discounted_price === 0 ? 'GRATIS' : `$${formatPrice(selectedFormat.discounted_price)}`}
                </span>
                {(product.formats.length > 1 || (selectedFormat.quantity !== 1 && selectedFormat.discounted_price > 0)) && (
                  <div className="mt-0.5 text-sm text-gray-500">
                    {product.formats.length > 1 && selectedFormat.name}
                    {product.formats.length > 1 && selectedFormat.quantity !== 1 && selectedFormat.discounted_price > 0 && ' · '}
                    {selectedFormat.quantity !== 1 && selectedFormat.discounted_price > 0 && (
                      `$${formatPrice(selectedFormat.discounted_price / selectedFormat.quantity)}/${selectedFormat.unit_name || 'u'}`
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Description */}
            {(contentEdit.getDisplayValue('description') || contentEdit.isEditMode) && (
              <div className={contentEdit.isEditMode ? 'mb-4' : ''}>
                <EditableField
                  value={contentEdit.getDisplayValue('description')}
                  onChange={(v) => contentEdit.updateField('description', v)}
                  isEditMode={contentEdit.isEditMode}
                  isDirty={contentEdit.isFieldDirty('description')}
                  multiline
                  className={contentEdit.isEditMode ? 'mb-1 text-sm leading-relaxed text-gray-600' : 'mb-4 text-sm leading-relaxed text-gray-600'}
                >
                  {contentEdit.getDisplayValue('description') ? (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: contentEdit.getDisplayValue('description').replace(/\n/g, '<br />'),
                      }}
                    />
                  ) : (
                    <span className="italic text-gray-400">Sin descripción. Hacé clic para agregar.</span>
                  )}
                </EditableField>
                {contentEdit.isEditMode && (
                  <button
                    type="button"
                    onClick={handleGenerateDescription}
                    disabled={isGenerating}
                    className="inline-flex items-center gap-1.5 rounded bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-200 disabled:opacity-50"
                  >
                    {isGenerating ? (
                      <><i className="fa fa-spinner fa-spin" /> Generando...</>
                    ) : (
                      <><i className="fa fa-magic" /> {contentEdit.getDisplayValue('description').trim() ? 'Mejorar con IA' : 'Generar con IA'}</>
                    )}
                  </button>
                )}
              </div>
            )}

            {/* Warnings */}
            {product.warnings.length > 0 && (
              <div className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-600">
                {product.warnings[0]}
              </div>
            )}

            {/* Health Benefits */}
            {product.health_info && (
              <div className="mb-4">
                <HealthBenefits
                  title={product.health_info.title}
                  description={product.health_info.description}
                  benefits={product.health_info.benefits}
                />
              </div>
            )}

            {/* Delivery Info */}
            <div className="mb-4">
              <DeliveryInfo
                deliveryPrice={deliveryPrice}
                deliveryOptions={deliveryOptions}
                userDelivery={userDelivery}
              />
            </div>

            {/* Format Selector (hidden for single-format products unless admin editing) */}
            {(product.formats.length > 1 || contentEdit.isEditMode) && (
              <div className="mb-3">
                <h3 className="mb-2 text-sm font-medium text-gray-700"><i className="fa fa-box mr-1.5 text-gray-400" />¿Cómo lo querés?</h3>
                <FormatSelector
                  formats={product.formats}
                  selectedFormatId={selectedFormat?.id ?? null}
                  onChange={setSelectedFormat}
                  editProps={contentEdit.isEditMode ? {
                    isEditMode: true,
                    getPrice: contentEdit.getFormatPrice,
                    onPriceChange: contentEdit.updateFormatPrice,
                    isPriceDirty: contentEdit.isFormatPriceDirty,
                  } : undefined}
                />
              </div>
            )}

            {/* Quantity + Subtotal */}
            <div className="mb-3 flex items-center justify-between gap-4">
              <div>
                <h3 className="mb-2 text-sm font-medium text-gray-700">Cantidad:</h3>
                <QuantityInput value={quantity} onChange={setQuantity} />
              </div>
              <div className="text-right">
                <h3 className="mb-2 text-sm font-medium text-gray-700">Total:</h3>
                <span className="text-xl font-bold text-green-600">
                  {subtotal > 0 ? `$${formatPrice(subtotal)}` : '$0,00'}
                </span>
              </div>
            </div>

            {/* Add to Cart */}
            <button
              type="button"
              onClick={handleAddToCart}
              disabled={quantity === 0 || !selectedFormat || isAdding}
              className={`mb-5 w-full rounded-lg px-4 py-3.5 text-base font-medium transition-colors ${
                added
                  ? 'bg-green-600 text-white'
                  : quantity === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700 active:bg-green-800'
              }`}
            >
              {isAdding ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="fa fa-spinner fa-spin" /> Agregando...
                </span>
              ) : added ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="fa fa-check" /> Agregado
                </span>
              ) : quantity === 0 ? (
                'Seleccioná cantidad'
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <i className="fa fa-shopping-cart" /> Agregar al carrito
                </span>
              )}
            </button>

          </div>
        </div>

        {/* Related Products */}
        <GroupedRelatedProducts
          groups={relatedProducts}
          sections={relatedSections}
          onAddToCart={handleRelatedAddToCart}
          isEditMode={contentEdit.isEditMode}
          onOrderChange={contentEdit.setRelatedOrder}
        />
      </div>

      <MobileToolbar
        categories={categories}
        activeCategoryId={product.category_id}
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        onSearchSubmit={handleSearchSubmit}
      />

      {/* Admin inline edit controls */}
      {isAdmin && (
        <div className="fixed bottom-6 right-6 z-50 hidden items-end gap-3 sm:flex flex-col">
          {/* Save bar */}
          {contentEdit.isDirty && contentEdit.saveStatus !== 'success' && (
            <div className="flex items-center gap-2 rounded-lg bg-white px-4 py-2 shadow-lg ring-1 ring-gray-200">
              <span className="text-sm text-gray-600">Cambios sin guardar</span>
              <button
                type="button"
                onClick={contentEdit.cancel}
                className="rounded px-3 py-1 text-sm text-gray-500 hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={contentEdit.save}
                disabled={contentEdit.isSaving}
                className="rounded bg-green-600 px-3 py-1 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
              >
                {contentEdit.isSaving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          )}

          {/* Success toast */}
          {contentEdit.saveStatus === 'success' && (
            <div className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-lg">
              Guardado
            </div>
          )}

          {/* Error toast */}
          {contentEdit.saveStatus === 'error' && (
            <div className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-lg">
              {contentEdit.errorMessage}
            </div>
          )}

          {/* Toggle button */}
          <button
            type="button"
            onClick={contentEdit.toggleEditMode}
            className={`flex h-12 w-12 items-center justify-center rounded-full shadow-lg transition-colors ${
              contentEdit.isEditMode
                ? 'bg-gray-800 text-white hover:bg-gray-700'
                : 'bg-white text-gray-600 hover:bg-gray-50 ring-1 ring-gray-200'
            }`}
            title={contentEdit.isEditMode ? 'Salir del modo edición' : 'Editar contenido'}
          >
            {contentEdit.isEditMode ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
            )}
          </button>
        </div>
      )}
    </MainLayout>
  );
}
